import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { setPayloadData, resetFieldErrorData } from "@store/slices/globalState";
import { updateProps } from "@actions/updateProps";
import DOMPurify from "isomorphic-dompurify";
import ImageContainer from "@components/imageContainer";
import CloneChildren from "@components/children";
import { AckoGarageStackListView } from "@widgets/ackoSelectGarages";
import { getWidgetSpecificIsVisible } from "@utils/index";
import { CommonProps } from "@utils/interface";
import * as S from "./styles";

type TabId = "coverages" | "claims" | "garages";

const PLAN_LEARN_MORE_TAB_KEY = "plan_learn_more_active_tab";

function consumeRestoredPlanLearnMoreTab(): TabId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PLAN_LEARN_MORE_TAB_KEY);
    if (raw == null) return null;
    sessionStorage.removeItem(PLAN_LEARN_MORE_TAB_KEY);
    const parsed = JSON.parse(raw);
    if (parsed === "garages" || parsed === "claims" || parsed === "coverages") {
      return parsed;
    }
  } catch {
    sessionStorage.removeItem(PLAN_LEARN_MORE_TAB_KEY);
  }
  return null;
}

export interface ICoveredItem {
  display_name: string;
  description: string;
  image: string;
  show_learn_more?: boolean;
}

export interface PlanLearnMoreProps extends CommonProps {
  plan_title?: string;
  dealer_make_name?: string;
  covered_items?: ICoveredItem[];
  not_covered_items?: ICoveredItem[];
  learn_more_page?: {
    claims_tab?: Record<string, unknown>;
    claim_payout?: Record<string, unknown>;
    show_dealer_badge?: boolean;
  };
  garage_list?: any[];
  pincode?: string;
}

const mapStateToProps = (state: any, ownProps: any) => ({
  is_visible: getWidgetSpecificIsVisible(
    state.configuration?.normalisedConfig,
    ownProps.id
  ),
});

const R = 40;
const SEMI_LEN = Math.PI * R;

function SemicircleGauge({
  percent,
  highlight,
}: {
  percent: number;
  highlight?: boolean;
}) {
  const dash = (Math.min(100, Math.max(0, percent)) / 100) * SEMI_LEN;
  const strokeBg = highlight ? "#c4b5fd" : "#e5e7eb";
  const strokeFill = highlight ? "#7c3aed" : "#5b21b6";
  return (
    <S.SemicircleWrap aria-hidden>
      <svg viewBox="0 0 100 58" width="100%" height="58">
        <path
          d={`M ${50 - R} 50 A ${R} ${R} 0 0 1 ${50 + R} 50`}
          fill="none"
          stroke={strokeBg}
          strokeWidth={8}
          strokeLinecap="round"
        />
        <path
          d={`M ${50 - R} 50 A ${R} ${R} 0 0 1 ${50 + R} 50`}
          fill="none"
          stroke={strokeFill}
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${SEMI_LEN}`}
        />
      </svg>
    </S.SemicircleWrap>
  );
}

function RepairWrenchIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
        stroke="#5b21b6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GaugePairRow({
  row,
}: {
  row: {
    label_left: string;
    percent: number;
    suffix_left: string;
    label_right: string;
    percent_right: number;
    suffix_right: string;
    highlight_right?: boolean;
  };
}) {
  return (
    <S.GaugeRow>
      <S.GaugeCol $highlight={false}>
        <SemicircleGauge percent={row.percent} />
        <S.GaugeBadge>
          {row.percent}% {row.suffix_left}
        </S.GaugeBadge>
        <S.GaugeFoot>{row.label_left}</S.GaugeFoot>
      </S.GaugeCol>
      <S.GaugeCol $highlight={row.highlight_right}>
        <SemicircleGauge percent={row.percent_right} highlight={row.highlight_right} />
        <S.GaugeBadge>
          {row.percent_right}% {row.suffix_right}
        </S.GaugeBadge>
        <S.GaugeFoot>{row.label_right}</S.GaugeFoot>
      </S.GaugeCol>
    </S.GaugeRow>
  );
}

function GaugeFourGrid({
  cells,
}: {
  cells: Array<{
    percent: number;
    badge: string;
    label: string;
    highlight?: boolean;
  }>;
}) {
  return (
    <S.GaugeGrid>
      {cells.map((cell, i) => (
        <S.GaugeCell key={i} $highlight={cell.highlight}>
          <SemicircleGauge percent={cell.percent} highlight={cell.highlight} />
          <S.GaugeBadge>{cell.badge}</S.GaugeBadge>
          <S.GaugeFoot>{cell.label}</S.GaugeFoot>
        </S.GaugeCell>
      ))}
    </S.GaugeGrid>
  );
}

const PlanLearnMore = (props: PlanLearnMoreProps) => {
  const {
    is_visible,
    plan_title,
    dealer_make_name,
    covered_items,
    not_covered_items,
    learn_more_page,
    garage_list,
    pincode,
    actions,
    children,
  } = props;

  const dispatch = useDispatch();

  const [tab, setTab] = useState<TabId>(
    () => consumeRestoredPlanLearnMoreTab() ?? "coverages"
  );

  const setTabTracked = (next: TabId) => {
    if (typeof window !== "undefined" && next !== "garages") {
      try {
        sessionStorage.removeItem(PLAN_LEARN_MORE_TAB_KEY);
      } catch {
        /* ignore */
      }
    }
    setTab(next);
  };

  if (is_visible === false) return null;

  const claimsTab = learn_more_page?.claims_tab as any;
  const claimPayout = learn_more_page?.claim_payout as any;
  const payoutLayout = claimPayout?.layout as string | undefined;
  const showBadge =
    learn_more_page?.show_dealer_badge && dealer_make_name;

  const onLearnMore = () => {
    const fn = actions?.on_learn_more_click;
    if (fn?.length) {
      fn.forEach((eachFunc: any) => {
        if (typeof eachFunc.funCall === "function") {
          eachFunc.funCall(eachFunc.args || {});
        }
      });
    }
  };

  const onPincodeClick = () => {
    dispatch(setPayloadData({ garage_pincode: "" }));
    dispatch(resetFieldErrorData());
    updateProps("plan_info_pincode_input_node", {
      payloadData: { name: "garage_pincode", value: "" },
      error_message: "",
    });
    try {
      if (typeof window !== "undefined" && tab === "garages") {
        sessionStorage.setItem(
          PLAN_LEARN_MORE_TAB_KEY,
          JSON.stringify("garages")
        );
      }
    } catch {
      /* ignore */
    }
    const fn = actions?.on_pincode_click;
    if (fn?.length) {
      fn.forEach((eachFunc: any) => {
        if (typeof eachFunc.funCall === "function") {
          eachFunc.funCall(eachFunc.args || {});
        }
      });
    }
  };

  const coveragePayoutBlock = () => {
    if (!claimPayout?.heading) return null;

    return (
      <>
        <S.SectionHeading>{claimPayout.heading}</S.SectionHeading>
        {claimPayout?.repair_card && (
          <S.MutedCard>
            <S.RepairIconWrap>
              <RepairWrenchIcon />
            </S.RepairIconWrap>
            <div>
              <S.Subheading style={{ marginBottom: 4 }}>
                {claimPayout.repair_card.title}
              </S.Subheading>
              <S.MutedText>{claimPayout.repair_card.description}</S.MutedText>
            </div>
          </S.MutedCard>
        )}
        {claimPayout?.replacement_intro && (
          <div>
            <S.Subheading>{claimPayout.replacement_intro.title}</S.Subheading>
            <S.MutedText style={{ marginBottom: 14 }}>
              {claimPayout.replacement_intro.description}
            </S.MutedText>
            {payoutLayout === "gauge_grid" && claimPayout?.gauge_cells?.length ? (
              <GaugeFourGrid cells={claimPayout.gauge_cells} />
            ) : (
              <S.GaugeTable>
                {claimPayout?.gauge_rows?.map((row: any, i: number) => (
                  <GaugePairRow key={i} row={row} />
                ))}
              </S.GaugeTable>
            )}
          </div>
        )}
      </>
    );
  };

  const claimsBody = () => {
    if (!claimsTab?.timeline_steps?.length) {
      return (
        <S.MutedText style={{ padding: "12px 0" }}>
          Claims information will appear here.
        </S.MutedText>
      );
    }
    return (
      <>
        <S.Subheading style={{ marginBottom: 8 }}>
          {claimsTab.manage_heading || "We manage everything for you"}
        </S.Subheading>
        <S.Timeline>
          {claimsTab.timeline_steps?.map((step: any, i: number) => (
            <S.TimelineItem key={i}>
              <S.TimelineDot />
              <S.TimelineBody>
                <S.TimelineTitle>{step.title}</S.TimelineTitle>
                {step.subtitle ? (
                  <S.TimelineSubtitle>
                    <span aria-hidden>🕐</span> {step.subtitle}
                  </S.TimelineSubtitle>
                ) : null}
                <S.TimelineDesc>{step.description}</S.TimelineDesc>
              </S.TimelineBody>
            </S.TimelineItem>
          ))}
        </S.Timeline>
        {claimsTab.timeline_banner ? (
          <S.BannerNote>{claimsTab.timeline_banner}</S.BannerNote>
        ) : null}

        {claimsTab.guide_cards?.length ? (
          <>
            <S.Subheading style={{ marginTop: 4 }}>
              {claimsTab.guide_heading || "Your claims guide"}
            </S.Subheading>
            <S.GuideScroll>
              {claimsTab.guide_cards.map((card: any, i: number) => (
                <S.GuideCard key={i}>
                  <S.GuideCardIcon aria-hidden>💡</S.GuideCardIcon>
                  <S.GuideCardTitle>{card.title}</S.GuideCardTitle>
                  <S.GuideCardBody>{card.body}</S.GuideCardBody>
                </S.GuideCard>
              ))}
            </S.GuideScroll>
          </>
        ) : null}

        {claimsTab.stats?.length ? (
          <>
            <S.Subheading>
              {claimsTab.stats_heading || "See why customers choose ACKO"}
            </S.Subheading>
            <S.StatsGrid>
              {claimsTab.stats.map((s: any, i: number) => (
                <S.StatCard key={i}>
                  {s.icon_image ? (
                    <S.StatIcon>
                      <img src={s.icon_image} alt="" width={20} height={20} />
                    </S.StatIcon>
                  ) : null}
                  <S.StatValue>
                    {s.value}
                    {s.suffix_star ? " ★" : ""}
                  </S.StatValue>
                  <S.StatLabel>{s.label}</S.StatLabel>
                </S.StatCard>
              ))}
            </S.StatsGrid>
          </>
        ) : null}
      </>
    );
  };

  return (
    <S.Outer>
      {showBadge && (
        <S.Badge>Same as your {dealer_make_name} dealer</S.Badge>
      )}
      {plan_title && <S.Title>{plan_title}</S.Title>}

      <S.TabRow>
        <S.Tab
          type="button"
          $active={tab === "coverages"}
          onClick={() => setTabTracked("coverages")}
        >
          Coverages
        </S.Tab>
        <S.Tab
          type="button"
          $active={tab === "claims"}
          onClick={() => setTabTracked("claims")}
        >
          Claims
        </S.Tab>
        <S.Tab
          type="button"
          $active={tab === "garages"}
          onClick={() => setTabTracked("garages")}
        >
          Garages
        </S.Tab>
      </S.TabRow>

      {tab === "coverages" && (
        <S.Panel>
          {coveragePayoutBlock()}

          <S.CoverageListGrid>
            <S.CoverageSectionTitle>What&apos;s covered</S.CoverageSectionTitle>
            {covered_items?.map((item, index) => (
              <S.CoverageListRow key={`c-${index}`}>
                <S.CoverageIconCell>
                  <ImageContainer
                    imgSrc={item.image}
                    alt=""
                    width={40}
                    height={40}
                  />
                </S.CoverageIconCell>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111827",
                    }}
                  >
                    {item.display_name}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#4b5563",
                      marginTop: 4,
                      lineHeight: "21px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.description || ""),
                    }}
                  />
                  {item.show_learn_more ? (
                    <button
                      type="button"
                      onClick={onLearnMore}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        marginTop: 4,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#451999",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Learn more
                    </button>
                  ) : null}
                </div>
              </S.CoverageListRow>
            ))}
          </S.CoverageListGrid>

          <S.CoverageListGrid>
            <S.CoverageSectionTitle>What&apos;s not covered</S.CoverageSectionTitle>
            {not_covered_items?.map((item, index) => (
              <S.CoverageListRow key={`nc-${index}`}>
                <S.CoverageIconCell>
                  <ImageContainer
                    imgSrc={item.image}
                    alt=""
                    width={40}
                    height={40}
                  />
                </S.CoverageIconCell>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111827",
                    }}
                  >
                    {item.display_name}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#4b5563",
                      marginTop: 4,
                      lineHeight: "21px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.description || ""),
                    }}
                  />
                </div>
              </S.CoverageListRow>
            ))}
          </S.CoverageListGrid>
        </S.Panel>
      )}

      {tab === "claims" && <S.Panel>{claimsBody()}</S.Panel>}

      {tab === "garages" && (
        <S.Panel>
          <S.GaragesSectionHeading>Authorised garages near you </S.GaragesSectionHeading>
          <S.LocationPinRow type="button" onClick={onPincodeClick} aria-label="Change pin code">
            <S.LocationIconWrap>
              <ImageContainer
                imgSrc="/images/location2.svg"
                alt=""
                width={20}
                height={20}
                styles={{ width: "20px", height: "20px" }}
                isResolvePath
              />
            </S.LocationIconWrap>
            <S.PincodeDisplayText>{pincode || "—"}</S.PincodeDisplayText>
            <S.LocationChevronWrap>
              <ImageContainer
                imgSrc="/images/arrow-down.svg"
                alt=""
                width={28}
                height={28}
                styles={{ width: "28px", height: "28px" }}
                isResolvePath
              />
            </S.LocationChevronWrap>
          </S.LocationPinRow>
          <AckoGarageStackListView garageList={garage_list} />
        </S.Panel>
      )}

      {children && <CloneChildren>{children}</CloneChildren>}
    </S.Outer>
  );
};

export default connect(mapStateToProps)(PlanLearnMore);
